import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../services/api';
import Layout from '../components/Layout';
import PhoneInput, { isValidPhoneNumber, getCountries, getCountryCallingCode } from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import DocumentUpload from '../components/DocumentUpload';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';
import './AddClient.css';
import { COUNTRY_CURRENCY_MAP, CURRENCY_LABELS } from '../utils/currencyMap';

// Line Icons Component
const Icon = ({ name, size = 20 }) => {
  const icons = {
    arrowLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
    save: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>,
  };
  return icons[name] || null;
};

const AddClient = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    mobileCountry: '',
    contractType: '',
    billingFrequency: '',
    contractValue: '',
    currency: 'INR',
    industry: '',
    location: '',
    address: '',
    gstVatEin: '',
    technologyStack: ''
  });

  // ... (inside the component)



  const [selectedMobileCountry, setSelectedMobileCountry] = useState('IN');
  const [phoneError, setPhoneError] = useState('');

  // Update currency when country changes
  useEffect(() => {
    if (selectedMobileCountry && COUNTRY_CURRENCY_MAP[selectedMobileCountry]) {
      setFormData(prev => ({ ...prev, currency: COUNTRY_CURRENCY_MAP[selectedMobileCountry] }));
    }
  }, [selectedMobileCountry]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        contractValue: parseFloat(formData.contractValue) || 0,
        technologyStack: formData.technologyStack ? formData.technologyStack.split(',').map(s => s.trim()) : [],
        communicationEmail: formData.email
      };

      await clientAPI.create(submitData);
      toast.success('Client added successfully!');
      navigate('/clients');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="add-client-page">
        <div className="page-header">
          <button className="btn-back" onClick={() => navigate('/clients')}>
            <Icon name="arrowLeft" size={20} />
            <span>Back to Clients</span>
          </button>
          <h1 className="page-title">Add New Client</h1>
          <p className="page-subtitle">Enter client information and contract details</p>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit} className="client-form">
            {/* Basic Information */}
            <div className="form-section">
              <h2 className="section-title">Basic Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Client Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter client name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Industry</label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    placeholder="e.g., Technology, Healthcare"
                  />
                </div>

                <div className="form-group">
                  <label>Contact Person <span className="required">*</span></label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    placeholder="Enter contact person name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="contact@company.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <div className={`premium-phone-input ${phoneError ? 'has-error' : ''}`}>
                    <div className="country-select-wrapper">
                      {selectedMobileCountry ? (
                        <>
                          <img
                            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedMobileCountry}.svg`}
                            alt={selectedMobileCountry}
                            className="flag-icon"
                          />
                          <span className="calling-code">+{getCountryCallingCode(selectedMobileCountry)}</span>
                        </>
                      ) : (
                        <div className="globe-icon">🌐</div>
                      )}
                      <select
                        className="country-native-select"
                        value={selectedMobileCountry || ''}
                        onChange={(e) => {
                          const newCountry = e.target.value || null;
                          setSelectedMobileCountry(newCountry);
                          setFormData(prev => ({ ...prev, mobileCountry: newCountry || '' }));
                          setPhoneError('');
                        }}
                        disabled={loading}
                      >
                        <option value="">Select Country</option>
                        {getCountries().map((country) => (
                          <option key={country} value={country}>
                            {en[country]} (+{getCountryCallingCode(country)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="phone-input-field-wrapper">
                      <Input
                        country={selectedMobileCountry}
                        placeholder={selectedMobileCountry ? `Enter number for ${en[selectedMobileCountry]}` : "Select country first"}
                        value={formData.phone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, phone: value || '' }));

                          // Real-time validation
                          if (value) {
                            if (isValidPhoneNumber(value)) {
                              setPhoneError('');
                            } else {
                              setPhoneError('Please enter a valid phone number for the selected country');
                            }
                          } else {
                            setPhoneError('');
                          }
                        }}
                        disabled={!selectedMobileCountry || loading}
                        className="PhoneInputInput"
                      />
                    </div>
                  </div>
                  {phoneError && <span className="error-text" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{phoneError}</span>}
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="City, State/Country"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter full address"
                />
              </div>
            </div>

            {/* Contract Details */}
            <div className="form-section">
              <h2 className="section-title">Contract Details</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Contract Type <span className="required">*</span></label>
                  <select
                    name="contractType"
                    value={formData.contractType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Fixed Price">Fixed Price</option>
                    <option value="Hourly Based">Hourly Based</option>
                    <option value="Resource-Based">Resource-Based</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Billing Frequency <span className="required">*</span></label>
                  <select
                    name="billingFrequency"
                    value={formData.billingFrequency}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Frequency</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Milestone-based">Milestone-based</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Currency</label>
                  <div className="fancy-select-wrapper">
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="currency-select"
                    >
                      {Object.entries(CURRENCY_LABELS).sort().map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Contract Value</label>
                  <div className="amount-input-wrapper">
                    <span className="currency-prefix">{formData.currency || 'INR'}</span>
                    <input
                      type="number"
                      name="contractValue"
                      value={formData.contractValue}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>GST/VAT/EIN</label>
                  <input
                    type="text"
                    name="gstVatEin"
                    value={formData.gstVatEin}
                    onChange={handleInputChange}
                    placeholder="Tax identification number"
                  />
                </div>
              </div>
            </div>

            {/* Technical Information */}
            <div className="form-section">
              <h2 className="section-title">Technical Information</h2>
              <div className="form-group full-width">
                <label>Technology Stack</label>
                <input
                  type="text"
                  name="technologyStack"
                  value={formData.technologyStack}
                  onChange={handleInputChange}
                  placeholder="e.g., React, Node.js, AWS (comma separated)"
                />
                <span className="form-hint">Enter technologies separated by commas</span>
              </div>
            </div>

            {/* Documents */}
            <div className="form-section">
              <h2 className="section-title">Documents</h2>
              <div className="form-group full-width">
                <label>Contracts & Agreements</label>
                <DocumentUpload
                  entityType="client"
                  onUploadComplete={(docs) => console.log('Client docs uploaded', docs)}
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => navigate('/clients')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={loading}
              >
                <Icon name="save" size={18} />
                <span>{loading ? 'Saving...' : 'Save Client'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AddClient;
